/**
 * Lightweight global perf counters, sampled by the PerfHud.
 *
 * These are plain integers bumped at instrumentation points (e.g. every socket
 * payload decompression). The HUD reads them on its 4×/sec sample tick and
 * resets them, turning the raw totals into per-second rates. Cost when nobody
 * reads them is a couple of integer adds — safe to leave compiled into every
 * build.
 *
 * Why this exists: idle heat in the lobby comes from continuous main-thread
 * work (a chatty socket decompressing messages for tables you're not even
 * looking at). The HUD's frame/tween metrics can't see that, so we count it
 * directly here.
 */
export const perfCounters = {
  /** Inbound payloads run through decompressData (≈ socket messages). */
  messages: 0,
  /** Total wall-clock ms spent inside decompressData. A CPU-load proxy. */
  decompressMs: 0
};

/** Record one decompression: bump the message count and accumulate its cost. */
export function recordDecompress(durationMs: number): void {
  perfCounters.messages += 1;
  perfCounters.decompressMs += durationMs;
}

/** Read the current totals and reset them. Returns counts since the last read. */
export function drainPerfCounters(): { messages: number; decompressMs: number } {
  const snapshot = { messages: perfCounters.messages, decompressMs: perfCounters.decompressMs };
  perfCounters.messages = 0;
  perfCounters.decompressMs = 0;
  return snapshot;
}

/**
 * Connection latency, surfaced to the HUD. The two sockets get two different
 * (and deliberately separate) measurements:
 *
 *   • actionMsgAt — Socket.IO "action" socket (legacy engine). We can't do a
 *     true round-trip here: the only events the engine acks are side-effecting
 *     (playerMove etc.), so there's no benign echo. Instead we stamp the time
 *     of the last inbound message and show its AGE — a stream-liveness signal,
 *     not a latency. 0 = nothing received yet.
 *   • wsRttMs / wsRttAt — API Gateway WebSocket ("update" socket). This one has
 *     a real `ping`→`pong` route, so we measure a true round-trip time. -1 =
 *     no measurement yet; wsRttAt lets the HUD flag a stale reading.
 *
 * These are plain state (current values), NOT rate counters — drainPerfCounters
 * leaves them alone; readLatency() reads without resetting.
 */
const latency = {
  actionMsgAt: 0,
  wsRttMs: -1,
  wsRttAt: 0
};

/** Stamp the arrival of an inbound Socket.IO action-socket message. */
export function recordActionMessage(): void {
  latency.actionMsgAt = Date.now();
}

/** Record a freshly measured API Gateway WebSocket round-trip time (ms). */
export function recordWsRtt(rttMs: number): void {
  latency.wsRttMs = rttMs;
  latency.wsRttAt = Date.now();
}

/** Read current latency state without mutating it. */
export function readLatency(): { actionMsgAt: number; wsRttMs: number; wsRttAt: number } {
  return { actionMsgAt: latency.actionMsgAt, wsRttMs: latency.wsRttMs, wsRttAt: latency.wsRttAt };
}

/**
 * RTT-probe gate. A true round-trip ping on the API Gateway socket costs a
 * server round-trip (the `ping` Lambda does an RDS write), so we only probe
 * frequently while someone is actually watching the HUD. The HUD flips this on
 * while mounted and off on cleanup; the WebSocket adapter subscribes and
 * starts/stops its probe timer in response — so NOTHING ticks when the HUD is
 * closed (not even a no-op interval). When off, the socket falls back to its
 * normal multi-minute keepalive, which still yields an occasional RTT sample.
 */
let latencyProbeEnabled = false;
const probeListeners = new Set<(enabled: boolean) => void>();

export function setLatencyProbe(enabled: boolean): void {
  if (latencyProbeEnabled === enabled) return; // no-op on unchanged state
  latencyProbeEnabled = enabled;
  probeListeners.forEach((listener) => {
    try {
      listener(enabled);
    } catch {
      // A misbehaving subscriber must never break the HUD toggle.
    }
  });
}

export function isLatencyProbeEnabled(): boolean {
  return latencyProbeEnabled;
}

/**
 * Subscribe to probe on/off changes. Returns an unsubscribe function. The
 * WebSocket adapter uses this to spin its RTT-probe timer up and down exactly
 * in step with the HUD, instead of running an always-on interval.
 */
export function subscribeLatencyProbe(listener: (enabled: boolean) => void): () => void {
  probeListeners.add(listener);
  return () => {
    probeListeners.delete(listener);
  };
}
